import type { JSX, ReactNode } from "react";
import { type FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AUTH_INPUT,
  AUTH_LABEL,
  AUTH_PRIMARY,
  AUTH_SECONDARY,
  AuthNotice,
  AuthShell,
} from "@/components/ui/native-auth/AuthShell";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { V3_MENU_PANEL, V3_MENU_ROW } from "@/components/ui/v3/flagged";
import { SIGN_UP_STARTED_KEY } from "@/lib/native-auth";
import {
  addNativePasskeyAfterSignUp,
  canOfferSignUpPasskey,
} from "@/lib/native-passkey";
import { occupationLabel } from "@/lib/occupation-label";
import { cn } from "@/lib/utils";
import { useNativeAuthStore } from "@/store/native-auth.store";
import { useOccupationsStore } from "@/store/occupations.store";

// A new email or Google identity is verified but has no ROSC account yet.
export const NativeRegistration = ({
  footer,
  handoff = false,
}: {
  footer: ReactNode;
  handoff?: boolean;
}): JSX.Element => {
  const { t } = useTranslation();
  const provisionAccount = useNativeAuthStore(
    (state) => state.provisionAccount,
  );
  const refresh = useNativeAuthStore((state) => state.refresh);
  const { occupations, loading, fetchAndSetOccupations } =
    useOccupationsStore();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [occupationId, setOccupationId] = useState("");
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [offerPasskey, setOfferPasskey] = useState(false);
  const [passkeyFailed, setPasskeyFailed] = useState(false);

  useEffect(() => {
    void fetchAndSetOccupations();
  }, [fetchAndSetOccupations]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setFailed(false);
    void provisionAccount({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      occupationId: Number(occupationId),
    })
      .then(async () => {
        // Checked after provisioning so the fresh-session deadline holds
        // when the user can still tap "Create passkey".
        if (!handoff && (await canOfferSignUpPasskey())) setOfferPasskey(true);
        else await finish();
      })
      .catch(() => setFailed(true))
      .finally(() => setBusy(false));
  };

  const finish = () => {
    setBusy(true);
    sessionStorage.removeItem(SIGN_UP_STARTED_KEY);
    return refresh().finally(() => setBusy(false));
  };

  const createPasskey = () => {
    setBusy(true);
    void addNativePasskeyAfterSignUp().then((created) => {
      if (created) void finish();
      else {
        setBusy(false);
        setPasskeyFailed(true);
      }
    });
  };

  if (offerPasskey)
    return (
      <AuthShell
        description={t("auth.native.register.passkey.description")}
        title={t("auth.native.register.passkey.title")}
      >
        {passkeyFailed ? (
          <>
            <AuthNotice className="mb-5">
              {t("auth.native.register.passkey.laterNote")}
            </AuthNotice>
            <button
              className={AUTH_PRIMARY}
              disabled={busy}
              type="button"
              onClick={() => void finish()}
            >
              {t("auth.native.register.passkey.continue")}
            </button>
          </>
        ) : (
          <div className="flex flex-col gap-3">
            <button
              className={AUTH_PRIMARY}
              disabled={busy}
              type="button"
              onClick={createPasskey}
            >
              {t("auth.native.register.passkey.create")}
            </button>
            <button
              className={AUTH_SECONDARY}
              disabled={busy}
              type="button"
              onClick={() => void finish()}
            >
              {t("auth.native.register.passkey.notNow")}
            </button>
          </div>
        )}
      </AuthShell>
    );

  return (
    <AuthShell
      description={t("auth.native.register.description")}
      footer={footer}
      title={t("auth.native.register.title")}
    >
      {failed && (
        <AuthNotice className="mb-5" tone="error">
          {t("auth.native.register.failed")}
        </AuthNotice>
      )}
      <form className="flex flex-col gap-4" onSubmit={submit}>
        <div>
          <label className={AUTH_LABEL} htmlFor="native-first-name">
            {t("auth.native.register.firstName")}
          </label>
          <input
            autoComplete="given-name"
            className={AUTH_INPUT}
            disabled={busy}
            id="native-first-name"
            maxLength={100}
            required
            value={firstName}
            onChange={(event) => setFirstName(event.target.value)}
          />
        </div>
        <div>
          <label className={AUTH_LABEL} htmlFor="native-last-name">
            {t("auth.native.register.lastName")}
          </label>
          <input
            autoComplete="family-name"
            className={AUTH_INPUT}
            disabled={busy}
            id="native-last-name"
            maxLength={100}
            value={lastName}
            onChange={(event) => setLastName(event.target.value)}
          />
        </div>
        <div>
          <label className={AUTH_LABEL} htmlFor="native-occupation">
            {t("signUp.form.occupation")}
          </label>
          {/* v3.2 select (sign-in screens are always v3.2): the field's
              look, the v3 menu panel and rows. */}
          <Select
            disabled={busy || loading}
            required
            value={occupationId || undefined}
            onValueChange={setOccupationId}
          >
            <SelectTrigger
              className={cn(
                AUTH_INPUT,
                "justify-between border-0 shadow-v3-hairline! data-[placeholder]:text-v3-text-3 [&>svg]:size-4 [&>svg]:text-v3-text-3 [&>svg]:opacity-100",
              )}
              data-testid="native-occupation"
              id="native-occupation"
            >
              <SelectValue
                placeholder={t("auth.native.register.occupationPlaceholder")}
              />
            </SelectTrigger>
            <SelectContent className={cn(V3_MENU_PANEL, "p-1")}>
              {occupations.map((occupation) => (
                <SelectItem
                  key={occupation.id}
                  className={cn(V3_MENU_ROW, "min-h-11 text-[15px] lg:min-h-9")}
                  value={String(occupation.id)}
                >
                  {occupationLabel(occupation.occupation, t)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <button
          className={AUTH_PRIMARY}
          disabled={busy || !firstName.trim() || !occupationId}
          type="submit"
        >
          {t("auth.native.register.submit")}
        </button>
      </form>
    </AuthShell>
  );
};
